import * as _ from 'lodash-es';
import React from 'react';
import TopicPostOverflowMenuContent from './TopicPostOverflowMenuContent';
import * as styles from './TopicPostSection.module.scss';
import TopicPostsEmptyState from './TopicPostsEmptyState';
import TopicReorderablePosts from './TopicReorderablePosts';
import useAppSelector from '../../app/redux/useAppSelector';
import CollapsibleButton from '../../components/CollapsibleButton';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import { MenuItem } from '../../components/Menu';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { TopicPostsOrder } from '../../graphql/generated/graphql';
import pinIcon from '../../icons/non-prefilled/16x16/pinOutline.svg';
import unpinIcon from '../../icons/non-prefilled/16x16/unpinOutline.svg';
import {
  PostPolicyAction,
  usePostPolicyCheck,
} from '../../post/postPolicyCheck';
import PostRow from '../../post/PostRow';
import PostSectionHeader from '../../post/PostSectionHeader';
import Link from '../../utils/router/Link';

const TOPIC_POST_SECTION_POST_FRAGMENT = gql(`
  fragment TopicPostSection_post on SlimPostFields {
    id
    views
    userViewedAt
    updatedAt
    ...PostRow_post
    ...TopicPostOverflowMenuContent_post
    ...usePostPolicyCheck_post
  }
`);

const TOPIC_POST_SECTION_FRAGMENT = gql(`
  fragment TopicPostSection_posts on TopicPostConnection {
    edges {
      ...TopicReorderablePosts_postEdges
      node {
        id
        ...TopicPostSection_post
      }
      pinnedOrder
      cursor
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
`);

const METADATA_ATTRIBUTE = {
  [TopicPostsOrder.Popularity]: 'popularity',
  [TopicPostsOrder.PublishedAt]: 'publishedAt',
  [TopicPostsOrder.Title]: 'updatedAt',
  [TopicPostsOrder.UpdatedAt]: 'updatedAt',
  [TopicPostsOrder.ViewedAt]: 'userViewedAt',
} as const;

interface TopicPostSectionProps {
  aboveTitle?: React.ReactNode;
  controls: React.ReactNode;
  fetchMore?: (() => void) | null;
  refetch: () => void;
  link?: string;
  orderBy: TopicPostsOrder;
  pinPost?: ((id: string, order: number) => Promise<unknown>) | null;
  unpinPost?: ((id: string) => void) | null;
  title: React.ReactNode;
  topicId: string;
  posts?: DocumentType<typeof TOPIC_POST_SECTION_FRAGMENT> | null;
  main?: boolean;
  loading: boolean;
}

const TopicPostSection = ({
  aboveTitle,
  controls,
  link,
  fetchMore,
  refetch,
  pinPost,
  unpinPost,
  orderBy,
  posts,
  main,
  title,
  topicId,
  loading,
}: TopicPostSectionProps) => {
  const postPolicyCheck = usePostPolicyCheck();
  const currentUser = useAppSelector((state) => state.session.user);

  const edges = _.compact(posts?.edges ?? []);

  const renderMeta = (
    post: DocumentType<typeof TOPIC_POST_SECTION_POST_FRAGMENT>,
  ) => (
    <PostRow.Meta
      attribute={METADATA_ATTRIBUTE[orderBy]}
      values={{
        publishedAt: post.publishedAt,
        updatedAt: post.updatedAt,
        userViewedAt: post.userViewedAt,
        popularity: post.views,
      }}
    />
  );

  const reorderableEdges = pinPost
    ? edges.filter((e) => e.pinnedOrder != null)
    : [];

  const maxPin = Math.max(
    -1,
    ...reorderableEdges.map(({ pinnedOrder }) => pinnedOrder ?? -1),
  );

  const staticEdges = _.compact(
    pinPost ? edges.filter((e) => e.pinnedOrder == null) : (edges ?? []),
  );

  return (
    <div>
      {!!title && (
        <PostSectionHeader controls={controls} aboveTitle={aboveTitle}>
          {link ? (
            <h4>
              <Link className={styles.titleLink} to={link}>
                {title}
              </Link>
            </h4>
          ) : (
            <h3>{title}</h3>
          )}
        </PostSectionHeader>
      )}
      {!loading && posts && (
        <>
          {edges.length === 0 && main && (
            <TopicPostsEmptyState topicId={topicId} />
          )}
          {edges.length > 0 && (
            <div className={styles.posts}>
              {pinPost && reorderableEdges.length > 0 && (
                <TopicReorderablePosts
                  orderPost={pinPost}
                  postEdges={reorderableEdges}
                  renderPostEdge={({ node: post }) => {
                    if (post == null) return <>{null}</>;

                    const canEditPost = postPolicyCheck(
                      PostPolicyAction.EditPost,
                      post,
                      currentUser,
                    );

                    return (
                      <PostRow
                        seriesHoverCardEnabled
                        meta={renderMeta(post)}
                        renderOverflowMenu={(close) => (
                          <TopicPostOverflowMenuContent
                            post={post}
                            onClose={close}
                            canEditPost={canEditPost}
                            extraMenuItems={
                              <>
                                {unpinPost && (
                                  <MenuItem
                                    icon={unpinIcon}
                                    onClick={() => {
                                      unpinPost(post.id);
                                      close();
                                    }}
                                  >
                                    Unpin
                                  </MenuItem>
                                )}
                              </>
                            }
                            refetch={refetch}
                          />
                        )}
                        pinned
                        post={post}
                      />
                    );
                  }}
                />
              )}
              {staticEdges.map(({ node: post, pinnedOrder }) => {
                if (post == null) return null;

                const canEditPost = postPolicyCheck(
                  PostPolicyAction.EditPost,
                  post,
                  currentUser,
                );

                return (
                  <PostRow
                    seriesHoverCardEnabled
                    meta={renderMeta(post)}
                    key={post.id}
                    renderOverflowMenu={(close) => (
                      <TopicPostOverflowMenuContent
                        post={post}
                        onClose={close}
                        canEditPost={canEditPost}
                        extraMenuItems={
                          <>
                            {pinPost && (
                              <MenuItem
                                icon={pinIcon}
                                onClick={() => {
                                  pinPost(post.id, maxPin + 1);
                                  close();
                                }}
                              >
                                Pin
                              </MenuItem>
                            )}
                          </>
                        }
                        refetch={refetch}
                      />
                    )}
                    pinned={main ? pinnedOrder != null : undefined}
                    post={post}
                  />
                );
              })}
              {fetchMore &&
                (main ? (
                  <InfiniteScrollWaypoint fetchMore={fetchMore} />
                ) : (
                  <div className={styles.loadMoreContainer}>
                    <CollapsibleButton onClick={() => fetchMore()} collapsed>
                      Load more
                    </CollapsibleButton>
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface TopicPostSectionSimpleListProps<
  Post extends DocumentType<typeof TOPIC_POST_SECTION_POST_FRAGMENT>,
> {
  title: React.ReactNode;
  aboveTitle?: React.ReactNode;
  controls: React.ReactNode;
  loading: boolean;
  posts: Post[];
  refetch: () => void;
  renderMeta: (post: Post) => React.ReactNode;
}

export const TopicPostSectionSimpleList = <
  Post extends DocumentType<typeof TOPIC_POST_SECTION_POST_FRAGMENT>,
>({
  title,
  aboveTitle,
  controls,
  posts,
  loading,
  refetch,
  renderMeta,
}: TopicPostSectionSimpleListProps<Post>) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();

  return (
    <div>
      {!!title && (
        <PostSectionHeader controls={controls} aboveTitle={aboveTitle}>
          <h3>{title}</h3>
        </PostSectionHeader>
      )}
      {!loading && posts.length > 0 && (
        <div className={styles.posts}>
          {posts.map((post) => {
            const canEditPost = postPolicyCheck(
              PostPolicyAction.EditPost,
              post,
              currentUser,
            );

            return (
              <PostRow
                seriesHoverCardEnabled
                meta={renderMeta(post)}
                key={post.id}
                renderOverflowMenu={(close) => (
                  <TopicPostOverflowMenuContent
                    post={post}
                    onClose={close}
                    canEditPost={canEditPost}
                    refetch={refetch}
                  />
                )}
                post={post}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TopicPostSection;
